import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SERVICES, CONTACT_INFO } from "@/data/content";
import { Phone, Mail, MapPin, Loader2, CheckCircle2, MessageCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [...SERVICES.map((s) => s.name), "Other / General Inquiry"];

const INITIAL_FORM = { name: "", email: "", phone: "", service: "", message: "", website: "" };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const ContactSection = ({ initialService }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Pre-select service when arriving from a service page
  // (location.state is primary; sessionStorage is a fallback for reliability)
  useEffect(() => {
    const stored = sessionStorage.getItem("gs-preselect-service");
    const candidate = initialService || stored;
    if (candidate && SERVICE_OPTIONS.includes(candidate)) {
      setForm((prev) => ({ ...prev, service: candidate }));
      setSubmitted(false);
    }
    if (stored) sessionStorage.removeItem("gs-preselect-service");
  }, [initialService]);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim() || form.name.trim().length < 2) errs.name = "Please enter your full name.";
    if (!EMAIL_RE.test(form.email.trim())) errs.email = "Please enter a valid email address.";
    if (!form.phone.trim() || form.phone.trim().length < 6) errs.phone = "Please enter a valid phone number.";
    if (!form.service) errs.service = "Please select a service.";
    if (!form.message.trim() || form.message.trim().length < 10)
      errs.message = "Please describe your requirements (at least 10 characters).";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        service: form.service,
        message: form.message.trim(),
        website: form.website, // honeypot
      });
      setSubmitted(true);
      setForm(INITIAL_FORM);
      toast.success("Quotation request sent! We'll get back to you within 24 hours.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Something went wrong sending your request. Please try again or contact us directly."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-[var(--section-py)]" data-testid="contact-section">
      <div className="mx-auto max-w-[var(--container-max)] px-4 sm:px-6">
        <div className="mb-12 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[color:var(--gs-gold-400)]">
            Get a Quotation
          </p>
          <h2 className="font-display mt-3 text-3xl font-bold text-white sm:text-4xl" data-testid="contact-heading">
            Let's grow your business together
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-white/60">
            Tell us what you need — we'll respond with a tailored proposal within 24 hours.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-7"
          >
            <div className="rounded-[var(--radius)] border border-white/10 bg-[color:var(--gs-glass)] p-6 shadow-[var(--shadow-elev-2)] backdrop-blur sm:p-8">
              {submitted ? (
                <div
                  className="flex flex-col items-center justify-center gap-4 py-14 text-center"
                  data-testid="contact-form-success"
                >
                  <div className="flex h-16 w-16 items-center justify-center rounded-full border border-[color:var(--gs-line)] bg-[rgba(240,180,41,0.12)]">
                    <CheckCircle2 className="h-8 w-8 text-[color:var(--gs-gold-400)]" />
                  </div>
                  <h3 className="font-display text-xl font-semibold text-white">
                    Request received!
                  </h3>
                  <p className="max-w-sm text-sm text-white/60" data-testid="contact-form-status-text">
                    Thank you for reaching out. Our team will review your requirements and
                    reply to your email within 24 hours.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => setSubmitted(false)}
                    data-testid="contact-form-send-another-button"
                    className="mt-2 border-white/15 bg-white/5 text-white hover:bg-white/10 hover:text-white"
                  >
                    Send another request
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate>
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-white/80">
                        Full Name <span className="text-[color:var(--gs-gold-400)]">*</span>
                      </Label>
                      <Input
                        id="name"
                        data-testid="contact-form-name-input"
                        placeholder="John Smith"
                        value={form.name}
                        onChange={(e) => setField("name", e.target.value)}
                        className="border-white/15 bg-white/5 text-white placeholder:text-white/30 focus-visible:ring-[color:var(--gs-gold-400)]"
                      />
                      {errors.name && (
                        <p className="text-xs text-red-400" data-testid="contact-form-name-error">{errors.name}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-white/80">
                        Email <span className="text-[color:var(--gs-gold-400)]">*</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="contact-form-email-input"
                        placeholder="john@company.com"
                        value={form.email}
                        onChange={(e) => setField("email", e.target.value)}
                        className="border-white/15 bg-white/5 text-white placeholder:text-white/30 focus-visible:ring-[color:var(--gs-gold-400)]"
                      />
                      {errors.email && (
                        <p className="text-xs text-red-400" data-testid="contact-form-email-error">{errors.email}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-white/80">
                        Phone / WhatsApp <span className="text-[color:var(--gs-gold-400)]">*</span>
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        data-testid="contact-form-phone-input"
                        placeholder="+1 555 000 0000"
                        value={form.phone}
                        onChange={(e) => setField("phone", e.target.value)}
                        className="border-white/15 bg-white/5 text-white placeholder:text-white/30 focus-visible:ring-[color:var(--gs-gold-400)]"
                      />
                      {errors.phone && (
                        <p className="text-xs text-red-400" data-testid="contact-form-phone-error">{errors.phone}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/80">
                        Service Needed <span className="text-[color:var(--gs-gold-400)]">*</span>
                      </Label>
                      <Select 
                        value={form.service} 
                        onValueChange={(val) => {
                          // Guard against spurious empty string resets from SelectBubbleInput
                          if (val === "") return;
                          setField("service", val);
                        }}
                      >
                        <SelectTrigger
                          data-testid="contact-form-service-select"
                          className="border-white/15 bg-white/5 text-white focus:ring-[color:var(--gs-gold-400)] data-[placeholder]:text-white/30"
                        >
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent className="max-h-64 border-white/15 bg-[color:var(--gs-navy-850)] text-white">
                          {SERVICE_OPTIONS.map((opt) => (
                            <SelectItem key={opt} value={opt}>
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.service && (
                        <p className="text-xs text-red-400" data-testid="contact-form-service-error">{errors.service}</p>
                      )}
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="message" className="text-white/80">
                        Requirements / Comments <span className="text-[color:var(--gs-gold-400)]">*</span>
                      </Label>
                      <Textarea
                        id="message"
                        rows={5}
                        data-testid="contact-form-message-textarea"
                        placeholder="Tell us about your project, goals, timeline, and budget..."
                        value={form.message}
                        onChange={(e) => setField("message", e.target.value)}
                        className="resize-none border-white/15 bg-white/5 text-white placeholder:text-white/30 focus-visible:ring-[color:var(--gs-gold-400)]"
                      />
                      {errors.message && (
                        <p className="text-xs text-red-400" data-testid="contact-form-message-error">{errors.message}</p>
                      )}
                    </div>
                    {/* Honeypot - hidden from real users */}
                    <input
                      type="text"
                      name="website"
                      value={form.website}
                      onChange={(e) => setField("website", e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }}
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-form-submit-button"
                    className="mt-7 h-12 w-full bg-[color:var(--gs-gold-400)] text-base font-semibold text-[color:var(--gs-navy-950)] hover:bg-[color:var(--gs-gold-500)] focus-visible:ring-2 focus-visible:ring-[color:var(--gs-gold-400)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--gs-navy-900)] active:scale-[0.99] disabled:opacity-60"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Sending...
                      </>
                    ) : (
                      "Request Free Quotation"
                    )}
                  </Button>
                  <p className="mt-3 text-center text-xs text-white/40">
                    We respect your privacy. Your information is only used to respond to your inquiry.
                  </p>
                </form>
              )}
            </div>
          </motion.div>

          {/* Contact details card */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-5"
          >
            <div className="flex h-full flex-col gap-4">
              <div className="rounded-[var(--radius)] border border-[color:var(--gs-line)] bg-[color:var(--gs-glass)] p-6 backdrop-blur sm:p-7">
                <h3 className="font-display text-lg font-semibold text-white">Contact Information</h3>
                <p className="mt-1 text-sm text-white/50">
                  Prefer to reach out directly? We're available on all channels.
                </p>
                <ul className="mt-6 space-y-5">
                  <li className="flex items-start gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[color:var(--gs-line)] bg-[rgba(240,180,41,0.1)]">
                      <Phone className="h-4.5 w-4.5 text-[color:var(--gs-gold-400)]" size={18} />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-white/40">Phone / WhatsApp</p>
                      <a
                        href={`tel:${CONTACT_INFO.phoneRaw}`}
                        data-testid="contact-info-phone"
                        className="text-sm font-medium text-white hover:text-[color:var(--gs-gold-400)]"
                      >
                        {CONTACT_INFO.phone}
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[color:var(--gs-line)] bg-[rgba(240,180,41,0.1)]">
                      <Mail className="h-4.5 w-4.5 text-[color:var(--gs-gold-400)]" size={18} />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-white/40">Email</p>
                      <a
                        href={`mailto:${CONTACT_INFO.email}`}
                        data-testid="contact-info-email"
                        className="break-all text-sm font-medium text-white hover:text-[color:var(--gs-gold-400)]"
                      >
                        {CONTACT_INFO.email}
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[color:var(--gs-line)] bg-[rgba(240,180,41,0.1)]">
                      <MapPin className="h-4.5 w-4.5 text-[color:var(--gs-gold-400)]" size={18} />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-white/40">Office</p>
                      <p className="text-sm font-medium leading-relaxed text-white" data-testid="contact-info-address">
                        {CONTACT_INFO.address}
                      </p>
                    </div>
                  </li>
                </ul>
              </div>

              {/* WhatsApp CTA card */}
              <a
                href={CONTACT_INFO.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-cta"
                className="group flex items-center gap-4 rounded-[var(--radius)] border border-white/10 bg-[color:var(--gs-glass)] p-6 backdrop-blur hover:border-[#25D366]/50"
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#25D366]/15">
                  <MessageCircle className="h-6 w-6 text-[#25D366]" />
                </div>
                <div>
                  <p className="font-display text-sm font-semibold text-white group-hover:text-[#25D366]">
                    Chat with us on WhatsApp
                  </p>
                  <p className="text-xs text-white/50">Fastest response — usually within minutes</p>
                </div>
              </a>

              {/* Business hours */}
              <div className="rounded-[var(--radius)] border border-white/10 bg-[color:var(--gs-glass)] p-6 backdrop-blur">
                <p className="font-display text-sm font-semibold text-white">Business Hours</p>
                <div className="mt-3 space-y-1.5 text-sm text-white/60" data-testid="business-hours">
                  <p className="flex justify-between"><span>Monday — Friday</span><span className="text-white/80">7:00 PM – 5:00 AM (Dhaka time)</span></p>
                  <p className="flex justify-between"><span>WhatsApp</span><span className="text-[color:var(--gs-gold-400)]">Fastest response</span></p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
